import { Link, useLocation } from "react-router";
import type { KeyboardEvent } from "react";

export interface TabItem {
  to: string;
  label: string;
  count?: number | string;
  /** Match the path exactly (use on the index tab). */
  end?: boolean;
}

/** A `to` without its query string; the query never decides which tab is active. */
function pathOf(to: string): string {
  const i = to.indexOf("?");
  return i === -1 ? to : to.slice(0, i);
}

/**
 * Whether `to` is the tab the current pathname is on.
 *
 * Without `end`, a tab also owns everything nested under it, so a run page
 * keeps Activity selected rather than leaving the strip with nothing marked.
 * Trailing slashes are normalised away on both sides.
 */
function isActivePath(pathname: string, to: string, end: boolean | undefined): boolean {
  const path = pathOf(to).replace(/\/$/, "");
  const here = pathname.replace(/\/$/, "");
  if (end) return here === path;
  return here === path || here.startsWith(`${path}/`);
}

/**
 * A route-aware tab strip (spec §5.4), with arrow-key movement between tabs
 * and a roving tabindex so the strip is one stop in the Tab order.
 *
 * Active state is computed from the pathname rather than delegated to
 * `NavLink`, which exposes `isActive` only to `className` and `children` —
 * `aria-selected` needs the value itself. A `to` may carry a query string; it
 * is preserved in the link and ignored when deciding which tab is active.
 */
export function Tabs({ items, label = "Sections" }: { items: TabItem[]; label?: string }) {
  const { pathname } = useLocation();

  /** Left/right arrows move focus along the strip and wrap at the ends, which is
   *  what a tablist is expected to do. */
  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'));
    const i = tabs.indexOf(document.activeElement as HTMLElement);
    if (i === -1) return;
    e.preventDefault();
    const next = e.key === "ArrowRight" ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
    tabs[next].focus();
  }

  return (
    <nav className="tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {items.map((t) => {
        const active = isActivePath(pathname, t.to, t.end);
        const showCount = t.count !== undefined && t.count !== 0 && t.count !== "0";
        return (
          <Link
            key={t.to}
            to={t.to}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            className={`tabs__tab${active ? " tabs__tab--active" : ""}`}
          >
            <span>{t.label}</span>
            {showCount ? <span className="tabs__count">{t.count}</span> : null}
          </Link>
        );
      })}
    </nav>
  );
}
